import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { NadiCustomEventsStore } from "../../hooks/useNadiCustomEvents";
import {
  buildNadiCustomEvent,
  emptyNadiEventDraft,
  NADI_HOUSE_FIELDS,
  nadiEventDraft,
  nadiEventKey,
} from "../../utils/nadiCustomEvents";

type Props = {
  store: NadiCustomEventsStore;
  selectedKey: string;
  onSelect: (key: string) => void;
};
export default function NadiCustomEventEditor({
  store,
  selectedKey,
  onSelect,
}: Props) {
  const { hasPaidAccess } = store;
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string>();
  const [draft, setDraft] = useState(emptyNadiEventDraft);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [removing, setRemoving] = useState(false);
  const activeAccount = useRef(store.accountKey);
  activeAccount.current = store.accountKey;
  const selected = store.events.find(
    (event) => nadiEventKey(event.id) === selectedKey,
  );
  useEffect(() => {
    setOpen(false);
    setDraft(emptyNadiEventDraft());
    setError("");
    setBusy(false);
  }, [store.accountKey, hasPaidAccess]);
  const start = (edit = false) => {
    setEditingId(edit ? selected?.id : undefined);
    setDraft(
      edit && selected ? nadiEventDraft(selected) : emptyNadiEventDraft(),
    );
    setError("");
    setRemoving(false);
    setOpen(true);
  };
  const save = async () => {
    const account = store.accountKey;
    try {
      setError("");
      setBusy(true);
      const saved = await store.saveEvent(
        buildNadiCustomEvent(draft),
        editingId,
      );
      if (activeAccount.current !== account) return;
      onSelect(nadiEventKey(saved.id));
      setOpen(false);
    } catch (error) {
      if (activeAccount.current === account) setError((error as Error).message);
    } finally {
      if (activeAccount.current === account) setBusy(false);
    }
  };
  const remove = async () => {
    const account = store.accountKey;
    if (!editingId) return;
    try {
      setError("");
      setBusy(true);
      await store.deleteEvent(editingId);
      if (activeAccount.current !== account) return;
      onSelect("none");
      setOpen(false);
    } catch (error) {
      if (activeAccount.current === account) setError((error as Error).message);
    } finally {
      if (activeAccount.current === account) setBusy(false);
    }
  };
  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button
          size="small"
          variant="outlined"
          disabled={!store.signedIn || !hasPaidAccess || store.loading}
          onClick={() => start()}
        >
          Add custom event
        </Button>
        {selected && hasPaidAccess && (
          <Button size="small" onClick={() => start(true)}>
            Edit custom event
          </Button>
        )}
      </Box>
      {!store.signedIn && (
        <Typography variant="caption">
          Sign in to save custom events to your profile.
        </Typography>
      )}
      {store.signedIn && !hasPaidAccess && (
        <Typography variant="caption">
          Custom events require an active paid plan.
        </Typography>
      )}
      {store.error && (
        <Alert
          severity="error"
          action={<Button onClick={() => void store.reload()}>Retry</Button>}
        >
          {store.error}
        </Alert>
      )}
      <Dialog
        open={open}
        onClose={() => {
          if (!busy) setOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingId ? "Edit custom event" : "Add custom event"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter houses from 1 to 12, separated by commas or spaces. Each house
            field is optional; fill at least one. Saved only to your profile.
          </Typography>
          <Box
            component="fieldset"
            disabled={busy}
            sx={{ border: 0, p: 0, m: 0, display: "grid", gap: 2 }}
          >
            <TextField
              label="Event name (optional)"
              placeholder="Custom event"
              value={draft.label}
              inputProps={{ maxLength: 80 }}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              fullWidth
            />
            {NADI_HOUSE_FIELDS.map(({ key, label }) => (
              <TextField
                key={key}
                label={label}
                placeholder="e.g. 2, 7, 11"
                value={draft[key]}
                onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                fullWidth
              />
            ))}
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {removing && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Remove this event from your profile? Saved chart calculations will
              stay unchanged.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
          {editingId && (
            <Button
              color="error"
              disabled={busy}
              onClick={() => (removing ? void remove() : setRemoving(true))}
            >
              {removing ? "Confirm remove" : "Remove event"}
            </Button>
          )}
          <Button disabled={busy} onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={busy || removing}
            onClick={() => void save()}
          >
            {busy ? "Saving…" : "Save event"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
